import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, AlertCircle, Bell, ChevronDown, ChevronUp, Send, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const TONE_BY_KEY = {
  d7:  { color: 'red',    label: '7-day URGENT' },
  d15: { color: 'amber',  label: '15-day' },
  d30: { color: 'orange', label: '30-day' },
  d60: { color: 'yellow', label: '60-day' },
};

const BUCKET_KEYS = ['d7', 'd15', 'd30', 'd60'];
const DOT = { d7: 'bg-red-500', d15: 'bg-amber-500', d30: 'bg-orange-500', d60: 'bg-yellow-500' };

const toneClasses = (tone) => ({
  red:    'bg-red-500/10 text-red-700 border-red-500/40',
  amber:  'bg-amber-500/10 text-amber-700 border-amber-500/40',
  orange: 'bg-orange-500/10 text-orange-700 border-orange-500/40',
  yellow: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/40',
}[tone] || 'bg-muted/40 text-muted-foreground border-border');

/**
 * Compact in-app banner listing units in each expiry-alert bucket.
 * Shown at the top of the Inventory tab when there's anything to flag.
 * Admin sees an extra "Dispatch email digest" button.
 */
export const AlertsBanner = ({ api, isAdmin, onViewAll }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [dispatching, setDispatching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/inventory/alerts');
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const dispatchDigest = async () => {
    if (!window.confirm('Send email digest to all Sales users and flagged Inventory Managers for every venue with units crossing the 60/30/15/7-day thresholds? This can only be undone by manually acknowledging the alerts_sent field on each unit.')) return;
    setDispatching(true);
    try {
      const res = await api.post('/inventory/alerts/dispatch?dry_run=false');
      toast.success(`Sent ${res.data.emails_sent} email${res.data.emails_sent === 1 ? '' : 's'} across ${res.data.venues_notified} venue${res.data.venues_notified === 1 ? '' : 's'}.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dispatch failed');
    } finally {
      setDispatching(false);
    }
  };

  if (loading) return null;
  const counts = data?.counts || {};
  const total = BUCKET_KEYS.reduce((sum, k) => sum + (counts[k] || 0), 0);
  if (total === 0) return null; // no banner when nothing's urgent

  // Accent follows the most urgent bucket that has units, so a 60-day-only list isn't shown as an emergency.
  const worst = BUCKET_KEYS.find(k => (counts[k] || 0) > 0);
  const accent = {
    d7:  { bar: 'border-l-red-500',    bg: 'bg-red-500/5',    icon: 'text-red-600' },
    d15: { bar: 'border-l-amber-500',  bg: 'bg-amber-500/5',  icon: 'text-amber-600' },
    d30: { bar: 'border-l-orange-500', bg: 'bg-orange-500/5', icon: 'text-orange-600' },
    d60: { bar: 'border-l-yellow-500', bg: 'bg-yellow-500/5', icon: 'text-yellow-600' },
  }[worst];

  return (
    <Card className={`border-l-4 ${accent.bar} ${accent.bg} overflow-hidden`} data-testid="ims-alerts-banner">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3">
        <button type="button" onClick={() => setExpanded(v => !v)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid="alerts-toggle-expand">
          <AlertCircle className={`w-5 h-5 shrink-0 ${accent.icon}`} />
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight">
              {total} unit{total !== 1 ? 's' : ''} approaching lock-in expiry
            </p>
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {BUCKET_KEYS.map(k => counts[k] > 0 && (
                <Badge key={k} className={`${toneClasses(TONE_BY_KEY[k].color)} text-[11px] font-medium`} data-testid={`alert-chip-${k}`}>
                  {TONE_BY_KEY[k].label} · {counts[k]}
                </Badge>
              ))}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1 shrink-0 ml-auto">
          {onViewAll && (
            <Button size="sm" variant="outline" className="h-8" onClick={onViewAll} data-testid="alerts-view-all-btn">
              View all expiries
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" className="h-8" onClick={dispatchDigest} disabled={dispatching} data-testid="alerts-dispatch-btn" title="Email the digest now">
              {dispatching ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Dispatch digest
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={load} title="Refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setExpanded(v => !v)} title={expanded ? 'Collapse' : 'Show units'}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border/60 bg-background/60 px-4 py-3 space-y-4" data-testid="alerts-detail">
          <div className="max-h-80 overflow-y-auto pr-1 space-y-4">
            {BUCKET_KEYS.map(k => (
              (data.buckets[k] || []).length > 0 && (
                <div key={k}>
                  <p className="text-[11px] font-semibold mb-1.5 uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${DOT[k]}`} />
                    {TONE_BY_KEY[k].label} · {(data.buckets[k] || []).length} unit{(data.buckets[k] || []).length !== 1 ? 's' : ''}
                  </p>
                  <div className="rounded-md border border-border overflow-hidden divide-y divide-border bg-card">
                    {data.buckets[k].map(u => (
                      <div key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`alert-row-${u.id}`}>
                        <span className="font-medium shrink-0">{u.name}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0">{u.category}</Badge>
                        <span className="text-xs text-muted-foreground truncate">
                          {u.client_name || '—'} · {u.venue_name} · {u.total_seats} seats
                        </span>
                        <span className={`ml-auto shrink-0 px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${toneClasses(TONE_BY_KEY[k].color)}`}>
                          {u.days_left}d left · {u.lockin_expiry_date}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground pt-2 border-t border-border">
            <Bell className="w-3 h-3 inline mr-1" /> Emails auto-dispatch daily. Use &ldquo;Dispatch digest&rdquo; to send immediately{isAdmin ? '' : ' (admin only)'}.
          </p>
        </div>
      )}
    </Card>
  );
};
